/**
 * F-parc.2b — Detalhe do parc grêle: cabeçalho + grade de forfaits (tela 2).
 * Cada forfait (até 5): preço cliente + débosselage + montage. O montage é
 * parte reservada do forfait (não soma); retido ES/BS = client − déb − montage.
 */

import { useEffect, useState } from "react";
import { Link, useParams } from "wouter";
import { ErpShell } from "../components/layout/ErpShell.js";
import { useModals } from "../components/common/ModalsProvider.js";
import { useCanEdit } from "../hooks/useCanEdit.js";
import { useSelectedCompany } from "../hooks/useSelectedCompany.js";
import { useAuth } from "../hooks/useAuth.js";
import { useT } from "../i18n/index.js";
import { readSheetRows } from "../lib/xlsx-to-csv.js";
import { trpc } from "../lib/trpc.js";

const inputClass =
  "w-full rounded-md border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-sm focus:border-lime outline-none disabled:opacity-50";

const POSITIONS = [1, 2, 3, 4, 5] as const;

interface Row {
  label: string;
  clientPrice: string;
  debosselagePrice: string;
  montagePrice: string;
}
const emptyRow: Row = { label: "", clientPrice: "", debosselagePrice: "", montagePrice: "" };

const toNum = (s: string) => {
  const n = Number(String(s).replace(",", "."));
  return Number.isFinite(n) ? n : 0;
};
const fmt = (n: number) =>
  new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

export default function HailParcDetail() {
  const params = useParams<{ id: string }>();
  const parcId = Number(params.id);
  const { user } = useAuth();
  const t = useT();
  const canEdit = useCanEdit();
  const companiesQuery = trpc.companies.list.useQuery();
  const accessible = (companiesQuery.data ?? []).filter((c) =>
    (user?.allowedCompanyIds ?? []).includes(c.id),
  );
  const [companyId] = useSelectedCompany(accessible);

  const parcQuery = trpc.hail.parcs.getById.useQuery(
    { companyId: companyId ?? 0, parcId },
    { enabled: companyId !== null && Number.isFinite(parcId) },
  );
  const utils = trpc.useUtils();

  const [rows, setRows] = useState<Row[]>(POSITIONS.map(() => ({ ...emptyRow })));
  const [error, setError] = useState<string | null>(null);
  const [savedMsg, setSavedMsg] = useState(false);

  // Preenche a grade a partir do que já existe no banco (por posição).
  useEffect(() => {
    const forfaits = parcQuery.data?.forfaits;
    if (!forfaits) return;
    const next = POSITIONS.map((pos) => {
      const f = forfaits.find((x) => x.position === pos);
      return f
        ? {
            label: f.label,
            clientPrice: String(Number(f.clientPrice)),
            debosselagePrice: String(Number(f.debosselagePrice)),
            montagePrice: String(Number(f.montagePrice)),
          }
        : { ...emptyRow };
    });
    setRows(next);
  }, [parcQuery.data?.parc.id, parcQuery.dataUpdatedAt]);

  const saveMutation = trpc.hail.forfaits.saveGrid.useMutation({
    onSuccess: () => {
      setSavedMsg(true);
      setTimeout(() => setSavedMsg(false), 2500);
      utils.hail.parcs.getById.invalidate();
    },
    onError: (e) => setError(e.message),
  });

  function updateRow(idx: number, patch: Partial<Row>) {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }

  function save() {
    if (companyId === null) return;
    setError(null);
    // F-grade-nome-vazio (CEO 2026-09-26, CHIR-AUTO P5): "Cat A…" no campo é
    // só o EXEMPLO — quem preenchia só os valores via a linha ser descartada em
    // silêncio e a grade voltar a zero. Linha com valor e sem nome ganha o
    // nome do exemplo; linha totalmente vazia continua ignorada.
    const forfaits = rows
      .map((r, i) => ({ row: r, position: POSITIONS[i]! }))
      .filter(
        ({ row }) =>
          row.label.trim() !== "" ||
          toNum(row.clientPrice) > 0 ||
          toNum(row.debosselagePrice) > 0 ||
          toNum(row.montagePrice) > 0,
      )
      .map(({ row, position }) => ({
        position,
        label:
          row.label.trim() ||
          `${t("hailParcDetail.catPlaceholder")} ${String.fromCharCode(64 + position)}`,
        clientPrice: toNum(row.clientPrice),
        debosselagePrice: toNum(row.debosselagePrice),
        montagePrice: toNum(row.montagePrice),
      }));
    const passa = forfaits.find((f) => f.debosselagePrice + f.montagePrice > f.clientPrice + 0.005);
    if (passa) {
      setError(`${passa.label}: ${t("hailParcDetail.techOverClient")}`);
      return;
    }
    saveMutation.mutate({ companyId, parcId, forfaits });
  }

  if (!user) return null;

  return (
    <ErpShell>
      <main className="max-w-5xl mx-auto p-6 space-y-5">
        <div className="text-xs text-neutral-500">
          <Link href="/parc-grele" className="hover:text-lime">
            ← {t("hailParcDetail.backToParcs")}
          </Link>
        </div>

        {parcQuery.isLoading && (
          <p className="text-sm text-neutral-500">{t("hailParcDetail.loading")}</p>
        )}
        {parcQuery.error && <p className="text-sm text-red-400">{parcQuery.error.message}</p>}

        {parcQuery.data && (
          <>
            <div className="ph">
              <div>
                <h1>{parcQuery.data.parc.name}</h1>
                <p>
                  {parcQuery.data.parc.status === "open"
                    ? t("hailParcDetail.statusOpen")
                    : t("hailParcDetail.statusClosed")}
                  {parcQuery.data.parc.eventDate ? ` · ${parcQuery.data.parc.eventDate}` : ""}
                </p>
              </div>
            </div>

            {/* Grade de forfaits */}
            <section className="rounded-lg border border-neutral-800 bg-neutral-900 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold uppercase tracking-wider text-neutral-400">
                  {t("hailParcDetail.forfaitGridTitle")}
                </h2>
                {savedMsg && (
                  <span className="text-xs text-lime">✓ {t("hailParcDetail.saved")}</span>
                )}
              </div>
              <p className="text-xs text-neutral-500">{t("hailParcDetail.forfaitGridHelp")}</p>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase tracking-wider text-neutral-500">
                    <tr>
                      <th className="text-left px-2 py-2 w-10">#</th>
                      <th className="text-left px-2 py-2">{t("hailParcDetail.colForfaitName")}</th>
                      <th className="text-right px-2 py-2 w-32">
                        {t("hailParcDetail.colClient")} €
                      </th>
                      <th className="text-right px-2 py-2 w-32">
                        {t("hailParcDetail.colDebosselage")} €
                      </th>
                      <th className="text-right px-2 py-2 w-32">
                        {t("hailParcDetail.colMontage")} €
                      </th>
                      <th className="text-right px-2 py-2 w-32">
                        {t("hailParcDetail.colRetained")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-800">
                    {rows.map((r, i) => {
                      const retained =
                        toNum(r.clientPrice) - toNum(r.debosselagePrice) - toNum(r.montagePrice);
                      const negative = retained < 0;
                      return (
                        <tr key={i}>
                          <td className="px-2 py-2 text-neutral-500">{POSITIONS[i]}</td>
                          <td className="px-2 py-2">
                            <input
                              value={r.label}
                              onChange={(e) => updateRow(i, { label: e.target.value })}
                              disabled={!canEdit}
                              placeholder={`${t("hailParcDetail.catPlaceholder")} ${String.fromCharCode(64 + POSITIONS[i]!)}`}
                              className={inputClass}
                            />
                          </td>
                          <td className="px-2 py-2">
                            <input
                              inputMode="decimal"
                              value={r.clientPrice}
                              onChange={(e) => updateRow(i, { clientPrice: e.target.value })}
                              disabled={!canEdit}
                              placeholder="0"
                              className={`${inputClass} text-right font-mono`}
                            />
                          </td>
                          <td className="px-2 py-2">
                            <input
                              inputMode="decimal"
                              value={r.debosselagePrice}
                              onChange={(e) => updateRow(i, { debosselagePrice: e.target.value })}
                              disabled={!canEdit}
                              placeholder="0"
                              className={`${inputClass} text-right font-mono`}
                            />
                          </td>
                          <td className="px-2 py-2">
                            <input
                              inputMode="decimal"
                              value={r.montagePrice}
                              onChange={(e) => updateRow(i, { montagePrice: e.target.value })}
                              disabled={!canEdit}
                              placeholder="0"
                              className={`${inputClass} text-right font-mono`}
                            />
                          </td>
                          <td
                            className={`px-2 py-2 text-right font-mono ${
                              negative ? "text-red-400" : "text-neutral-300"
                            }`}
                          >
                            {fmt(retained)} €
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {error && (
                <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
                  {error}
                </div>
              )}

              {canEdit && (
                <div className="flex justify-end">
                  <button
                    onClick={save}
                    disabled={saveMutation.isPending}
                    className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-50 px-5 py-2 text-sm font-medium"
                  >
                    {saveMutation.isPending
                      ? t("hailParcDetail.saving")
                      : t("hailParcDetail.saveGridBtn")}
                  </button>
                </div>
              )}
            </section>

            {/* Veículos do parc */}
            {companyId !== null && (
              <VehiclesSection
                companyId={companyId}
                parcId={parcId}
                forfaits={parcQuery.data.forfaits}
                canEdit={canEdit}
              />
            )}

            {/* Fatura da carrosserie */}
            {companyId !== null && (
              <InvoicesSection companyId={companyId} parcId={parcId} canEdit={canEdit} />
            )}
          </>
        )}
      </main>
    </ErpShell>
  );
}

type Forfait = { id: number; label: string; position: number };

function VehiclesSection({
  companyId,
  parcId,
  forfaits,
  canEdit,
}: {
  companyId: number;
  parcId: number;
  forfaits: Forfait[];
  canEdit: boolean;
}) {
  const t = useT();
  const utils = trpc.useUtils();
  const listQuery = trpc.hail.vehicles.list.useQuery({ companyId, parcId });
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    plate: "",
    vin: "",
    make: "",
    model: "",
    color: "",
    forfaitId: "" as number | "",
    debosselagePrice: "",
    montagePrice: "",
  });

  const addMutation = trpc.hail.vehicles.add.useMutation({
    onSuccess: () => {
      setForm((f) => ({ ...f, plate: "", vin: "", make: "", model: "", color: "" }));
      setError(null);
      utils.hail.vehicles.list.invalidate();
    },
    onError: (e) => setError(e.message),
  });
  const removeMutation = trpc.hail.vehicles.remove.useMutation({
    onSuccess: () => utils.hail.vehicles.list.invalidate(),
    onError: (e) => setError(e.message),
  });

  function addVehicle(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (form.forfaitId === "") {
      setError(t("hailParcDetail.errChooseForfait"));
      return;
    }
    if (!form.plate.trim() && !form.vin.trim()) {
      setError(t("hailParcDetail.errPlateOrVin"));
      return;
    }
    addMutation.mutate({
      companyId,
      parcId,
      forfaitId: Number(form.forfaitId),
      plate: form.plate.trim() || undefined,
      vin: form.vin.trim() || undefined,
      make: form.make.trim() || undefined,
      model: form.model.trim() || undefined,
      color: form.color.trim() || undefined,
      debosselagePrice: form.debosselagePrice ? toNum(form.debosselagePrice) : undefined,
      montagePrice: form.montagePrice ? toNum(form.montagePrice) : undefined,
    });
  }

  const rows = listQuery.data ?? [];
  const [showImport, setShowImport] = useState(false);

  return (
    <section className="rounded-lg border border-neutral-800 bg-neutral-900 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-neutral-400">
          {t("hailParcDetail.vehiclesTitle")} ({rows.length})
        </h2>
        {canEdit && forfaits.length > 0 && (
          <button
            onClick={() => setShowImport(true)}
            className="rounded-md border border-neutral-700 hover:border-lime hover:text-lime px-3 py-1.5 text-xs"
          >
            ⬆ {t("hailParcDetail.importSheetBtn")}
          </button>
        )}
      </div>

      {showImport && (
        <ImportModal
          companyId={companyId}
          parcId={parcId}
          onClose={() => setShowImport(false)}
          onDone={() => utils.hail.vehicles.list.invalidate()}
        />
      )}

      {rows.length > 0 &&
        (() => {
          const totalClient = rows.reduce((s, v) => s + Number(v.clientPrice), 0);
          const totalDebos = rows.reduce((s, v) => s + Number(v.debosselagePrice), 0);
          const totalMontage = rows.reduce((s, v) => s + Number(v.montagePrice), 0);
          const retained = totalClient - totalDebos - totalMontage;
          const Stat = ({
            label,
            value,
            tone,
          }: {
            label: string;
            value: string;
            tone?: string;
          }) => (
            <div className="rounded-md border border-neutral-800 bg-neutral-950/40 p-2.5">
              <p className="text-[10px] uppercase tracking-wider text-neutral-500">{label}</p>
              <p className={`text-base font-semibold font-mono ${tone ?? "text-neutral-100"}`}>
                {value}
              </p>
            </div>
          );
          return (
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              <Stat label={t("hailParcDetail.vehiclesTitle")} value={String(rows.length)} />
              <Stat label={t("hailParcDetail.statTotalClient")} value={`${fmt(totalClient)} €`} />
              <Stat
                label={t("hailParcDetail.statToPayDebos")}
                value={`${fmt(totalDebos)} €`}
                tone="text-amber-300"
              />
              <Stat
                label={t("hailParcDetail.statToPayMontage")}
                value={`${fmt(totalMontage)} €`}
                tone="text-amber-300"
              />
              <Stat
                label={t("hailParcDetail.colRetained")}
                value={`${fmt(retained)} €`}
                tone={retained < 0 ? "text-red-400" : "text-lime"}
              />
            </div>
          );
        })()}

      {canEdit && forfaits.length === 0 && (
        <p className="text-xs text-amber-300">{t("hailParcDetail.noForfaitsWarning")}</p>
      )}

      {canEdit && forfaits.length > 0 && (
        <form
          onSubmit={addVehicle}
          className="flex flex-wrap items-end gap-2 rounded-md border border-neutral-800 bg-neutral-950/40 p-3"
        >
          <FieldMini label={t("hailParcDetail.labelPlate")}>
            <input
              value={form.plate}
              onChange={(e) => setForm({ ...form, plate: e.target.value })}
              className={inputClass + " w-28"}
              placeholder="AA-123-AA"
            />
          </FieldMini>
          <FieldMini label={t("hailParcDetail.labelVin")}>
            <input
              value={form.vin}
              onChange={(e) => setForm({ ...form, vin: e.target.value.toUpperCase() })}
              maxLength={17}
              className={inputClass + " w-44 font-mono"}
              placeholder="VF1..."
            />
          </FieldMini>
          <FieldMini label={t("hailParcDetail.labelMake")}>
            <input
              value={form.make}
              onChange={(e) => setForm({ ...form, make: e.target.value })}
              className={inputClass + " w-28"}
            />
          </FieldMini>
          <FieldMini label={t("hailParcDetail.labelModel")}>
            <input
              value={form.model}
              onChange={(e) => setForm({ ...form, model: e.target.value })}
              className={inputClass + " w-28"}
            />
          </FieldMini>
          <FieldMini label={t("hailParcDetail.labelForfait")}>
            <select
              value={form.forfaitId}
              onChange={(e) =>
                setForm({ ...form, forfaitId: e.target.value ? Number(e.target.value) : "" })
              }
              className={inputClass + " w-32"}
            >
              <option value="">—</option>
              {forfaits.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.label}
                </option>
              ))}
            </select>
          </FieldMini>
          <FieldMini label={`${t("hailParcDetail.labelDebosShort")} €`}>
            <input
              inputMode="decimal"
              value={form.debosselagePrice}
              onChange={(e) => setForm({ ...form, debosselagePrice: e.target.value })}
              className={inputClass + " w-24 text-right font-mono"}
              placeholder={t("hailParcDetail.autoPlaceholder")}
            />
          </FieldMini>
          <FieldMini label={`${t("hailParcDetail.colMontage")} €`}>
            <input
              inputMode="decimal"
              value={form.montagePrice}
              onChange={(e) => setForm({ ...form, montagePrice: e.target.value })}
              className={inputClass + " w-24 text-right font-mono"}
              placeholder={t("hailParcDetail.autoPlaceholder")}
            />
          </FieldMini>
          <button
            type="submit"
            disabled={addMutation.isPending}
            className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-50 px-4 py-1.5 text-sm font-medium"
          >
            {addMutation.isPending ? "…" : `+ ${t("hailParcDetail.addBtn")}`}
          </button>
        </form>
      )}

      {error && (
        <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
          {error}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="text-left px-2 py-2">{t("hailParcDetail.labelPlate")}</th>
              <th className="text-left px-2 py-2">VIN</th>
              <th className="text-left px-2 py-2">{t("hailParcDetail.colVehicle")}</th>
              <th className="text-left px-2 py-2">{t("hailParcDetail.labelForfait")}</th>
              <th className="text-right px-2 py-2">{t("hailParcDetail.labelDebosShort")} €</th>
              <th className="text-right px-2 py-2">{t("hailParcDetail.colMontage")} €</th>
              {canEdit && <th className="px-2 py-2 w-10" />}
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-800">
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-2 py-3 text-sm text-neutral-500">
                  {t("hailParcDetail.emptyVehicles")}
                </td>
              </tr>
            )}
            {rows.map((v) => (
              <tr key={v.id}>
                <td className="px-2 py-2">{v.plate ?? "—"}</td>
                <td className="px-2 py-2 font-mono text-xs text-neutral-400">{v.vin ?? "—"}</td>
                <td className="px-2 py-2 text-neutral-300">
                  {[v.make, v.model].filter(Boolean).join(" ") || "—"}
                </td>
                <td className="px-2 py-2">{v.forfaitLabel ?? "—"}</td>
                <td className="px-2 py-2 text-right font-mono">
                  {fmt(Number(v.debosselagePrice))}
                </td>
                <td className="px-2 py-2 text-right font-mono">{fmt(Number(v.montagePrice))}</td>
                {canEdit && (
                  <td className="px-2 py-2 text-right">
                    <button
                      onClick={() =>
                        removeMutation.mutate({ companyId, parcId, hailVehicleId: v.id })
                      }
                      className="text-xs text-neutral-500 hover:text-red-400"
                      title={t("hailParcDetail.removeFromParcTitle")}
                    >
                      ✕
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function FieldMini({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-wider text-neutral-500 mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

// ── Import de planilha (xlsx/csv) ────────────────────────────────────────────

interface ImportRow {
  plate?: string;
  vin?: string;
  make?: string;
  model?: string;
  color?: string;
  forfaitLabel: string;
  debosselagePrice?: number;
  montagePrice?: number;
}

const normKey = (s: string) =>
  s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const HEADER_ALIASES: Record<keyof ImportRow, string[]> = {
  plate: ["plaque", "placa", "immatriculation"],
  vin: ["vin", "chassis", "chassi", "chasis"],
  make: ["marque", "marca", "make"],
  model: ["modele", "modelo", "model"],
  color: ["couleur", "cor", "color"],
  forfaitLabel: ["forfait", "categorie", "cat", "forfaitnom"],
  debosselagePrice: ["debosselage", "debosselageeur", "debos"],
  montagePrice: ["montage", "montageeur", "demontage"],
};

const parseNum = (s: string | undefined): number | undefined => {
  if (s === undefined) return undefined;
  const t = String(s).trim().replace(/\s/g, "").replace(",", ".");
  if (t === "") return undefined;
  const n = Number(t);
  return Number.isFinite(n) ? n : undefined;
};

/** Mapeia linhas cruas (keyed por cabeçalho) para o payload de import. */
function mapRows(raw: Record<string, string>[]): ImportRow[] {
  return (
    raw
      .map((r) => {
        const byNorm: Record<string, string> = {};
        for (const [k, v] of Object.entries(r)) byNorm[normKey(k)] = String(v ?? "").trim();
        const pick = (field: keyof ImportRow) => {
          for (const a of HEADER_ALIASES[field]) if (byNorm[a] !== undefined) return byNorm[a];
          return "";
        };
        return {
          plate: pick("plate") || undefined,
          vin: (pick("vin") || undefined)?.toUpperCase(),
          make: pick("make") || undefined,
          model: pick("model") || undefined,
          color: pick("color") || undefined,
          forfaitLabel: pick("forfaitLabel"),
          debosselagePrice: parseNum(pick("debosselagePrice")),
          montagePrice: parseNum(pick("montagePrice")),
        } as ImportRow;
      })
      // Ignora linhas totalmente vazias.
      .filter((r) => r.plate || r.vin || r.forfaitLabel)
  );
}

function ImportModal({
  companyId,
  parcId,
  onClose,
  onDone,
}: {
  companyId: number;
  parcId: number;
  onClose: () => void;
  onDone: () => void;
}) {
  const t = useT();
  const [rows, setRows] = useState<ImportRow[] | null>(null);
  const [fileName, setFileName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    created: number;
    skipped: number;
    errors: { line: number; message: string }[];
  } | null>(null);

  const importMutation = trpc.hail.vehicles.importBatch.useMutation({
    onSuccess: (res) => {
      setResult(res);
      onDone();
    },
    onError: (e) => setError(e.message),
  });

  async function onFile(file: File | undefined) {
    if (!file) return;
    setError(null);
    setResult(null);
    setFileName(file.name);
    try {
      const raw = await readSheetRows(file);
      setRows(mapRows(raw));
    } catch (e) {
      setError(e instanceof Error ? e.message : t("hailParcDetail.errReadFile"));
      setRows(null);
    }
  }

  const missingForfait = rows?.filter((r) => !r.forfaitLabel).length ?? 0;
  const missingId = rows?.filter((r) => !r.plate && !r.vin).length ?? 0;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
      <div className="w-full max-w-lg bg-neutral-950 border border-neutral-800 rounded-lg shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-neutral-800 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{t("hailParcDetail.importModalTitle")}</h2>
          <button onClick={onClose} className="text-neutral-500 hover:text-neutral-300 text-lg">
            ×
          </button>
        </div>
        <div className="p-5 space-y-4">
          <a
            href="/modele-parc-grele.xlsx"
            download
            className="inline-block text-xs text-lime hover:underline"
          >
            ⬇ {t("hailParcDetail.downloadTemplate")}
          </a>

          <div>
            <input
              type="file"
              accept=".xlsx,.xls,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => void onFile(e.target.files?.[0])}
              className="block w-full text-xs text-neutral-400 file:mr-3 file:rounded-md file:border file:border-neutral-700 file:bg-neutral-800 file:px-3 file:py-1.5 file:text-neutral-200"
            />
          </div>

          {error && (
            <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
              {error}
            </div>
          )}

          {rows && !result && (
            <div className="rounded-md border border-neutral-800 p-3 space-y-1 text-sm">
              <p className="text-neutral-300">
                <strong>{rows.length}</strong> {t("hailParcDetail.linesReadFrom")}{" "}
                <span className="text-neutral-500">{fileName}</span>.
              </p>
              {missingId > 0 && (
                <p className="text-amber-300 text-xs">
                  {missingId} {t("hailParcDetail.missingIdSuffix")}
                </p>
              )}
              {missingForfait > 0 && (
                <p className="text-amber-300 text-xs">
                  {missingForfait} {t("hailParcDetail.missingForfaitSuffix")}
                </p>
              )}
            </div>
          )}

          {result && (
            <div className="rounded-md border border-neutral-800 p-3 space-y-1 text-sm">
              <p className="text-lime">
                ✓ {result.created} {t("hailParcDetail.importedSuffix")}
              </p>
              {result.skipped > 0 && (
                <p className="text-neutral-400 text-xs">
                  {result.skipped} {t("hailParcDetail.skippedSuffix")}
                </p>
              )}
              {result.errors.length > 0 && (
                <div className="text-xs text-red-400 mt-1 max-h-40 overflow-y-auto">
                  <p className="font-medium">
                    {result.errors.length} {t("hailParcDetail.errorsCountSuffix")}
                  </p>
                  <ul className="list-disc list-inside">
                    {result.errors.slice(0, 30).map((er, i) => (
                      <li key={i}>
                        {t("hailParcDetail.linePrefix")} {er.line}: {er.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-neutral-800">
            <button
              onClick={onClose}
              className="rounded-md border border-neutral-800 hover:border-neutral-700 px-4 py-1.5 text-sm"
            >
              {result ? t("hailParcDetail.close") : t("hailParcDetail.cancel")}
            </button>
            {!result && (
              <button
                onClick={() => rows && importMutation.mutate({ companyId, parcId, rows })}
                disabled={!rows || rows.length === 0 || importMutation.isPending}
                className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-50 px-4 py-1.5 text-sm font-medium"
              >
                {importMutation.isPending
                  ? t("hailParcDetail.importing")
                  : `${t("hailParcDetail.importBtn")} ${rows?.length ?? 0}`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Fatura da carrosserie ────────────────────────────────────────────────────

function InvoicesSection({
  companyId,
  parcId,
  canEdit,
}: {
  companyId: number;
  parcId: number;
  canEdit: boolean;
}) {
  const t = useT();
  const utils = trpc.useUtils();
  const modals = useModals();
  const q = trpc.hail.invoices.getByParc.useQuery({ companyId, parcId });
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const generateMut = trpc.hail.invoices.generate.useMutation({
    onSuccess: () => {
      setError(null);
      utils.hail.invoices.getByParc.invalidate();
    },
    onError: (e) => setError(e.message),
  });
  const cancelMut = trpc.hail.invoices.cancel.useMutation({
    onSuccess: () => utils.hail.invoices.getByParc.invalidate(),
    onError: (e) => setError(e.message),
  });

  async function downloadPdf(invoiceId: number) {
    setDownloading(true);
    try {
      const res = await utils.hail.invoices.pdf.fetch({ companyId, parcId, invoiceId });
      const bytes = Uint8Array.from(atob(res.base64), (c) => c.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = res.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : t("hailParcDetail.errPdf"));
    } finally {
      setDownloading(false);
    }
  }

  const data = q.data;

  return (
    <section className="rounded-lg border border-neutral-800 bg-neutral-900 p-4 space-y-3">
      <h2 className="text-sm font-semibold uppercase tracking-wider text-neutral-400">
        {t("hailParcDetail.invoiceSectionTitle")}
      </h2>

      {error && (
        <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
          {error}
        </div>
      )}

      {!data && (
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-neutral-500">{t("hailParcDetail.noInvoiceHint")}</p>
          {canEdit && (
            <button
              onClick={() => generateMut.mutate({ companyId, parcId })}
              disabled={generateMut.isPending}
              className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-50 px-4 py-1.5 text-sm font-medium shrink-0"
            >
              {generateMut.isPending
                ? t("hailParcDetail.generating")
                : t("hailParcDetail.generateInvoiceBtn")}
            </button>
          )}
        </div>
      )}

      {data && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-base font-semibold text-neutral-100">
                {data.invoice.invoiceNumber}
              </p>
              <p className="text-xs text-neutral-500">
                {t("hailParcDetail.issuedOnPrefix")}{" "}
                {new Date(data.invoice.issuedAt).toLocaleDateString("fr-FR")} ·{" "}
                {data.invoice.vehicleCount} {t("hailParcDetail.vehiclesCountSuffix")}
                {data.invoice.dueAt
                  ? ` · ${t("hailParcDetail.dueLabel")} ${data.invoice.dueAt}`
                  : ""}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => void downloadPdf(data.invoice.id)}
                disabled={downloading}
                className="rounded-md border border-lime/40 text-lime hover:bg-lime/5 disabled:opacity-50 px-3 py-1.5 text-sm"
              >
                {downloading ? "…" : `📄 ${t("hailParcDetail.downloadPdfBtn")}`}
              </button>
              {canEdit && (
                <button
                  onClick={async () => {
                    const ok = await modals.confirm({
                      title: t("hailParcDetail.confirmCancelTitle"),
                      message: `${t("hailParcDetail.confirmCancelMsgPre")} ${data.invoice.invoiceNumber}? ${t("hailParcDetail.confirmCancelMsgSuf")}`,
                      confirmLabel: t("hailParcDetail.cancelInvoiceBtn"),
                      tone: "danger",
                    });
                    if (!ok) return;
                    cancelMut.mutate({ companyId, parcId, invoiceId: data.invoice.id });
                  }}
                  disabled={cancelMut.isPending}
                  className="rounded-md border border-neutral-700 hover:border-red-500 hover:text-red-400 disabled:opacity-50 px-3 py-1.5 text-sm"
                >
                  {t("hailParcDetail.cancel")}
                </button>
              )}
            </div>
          </div>

          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="text-left px-2 py-1">{t("hailParcDetail.labelForfait")}</th>
                <th className="text-right px-2 py-1">{t("hailParcDetail.colQty")}</th>
                <th className="text-right px-2 py-1">{t("hailParcDetail.colUnitPriceHt")}</th>
                <th className="text-right px-2 py-1">{t("hailParcDetail.colTotalHt")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-800">
              {data.lines.map((l) => (
                <tr key={l.id}>
                  <td className="px-2 py-1">{l.forfaitLabel}</td>
                  <td className="px-2 py-1 text-right">{l.quantity}</td>
                  <td className="px-2 py-1 text-right font-mono">{fmt(Number(l.unitPrice))} €</td>
                  <td className="px-2 py-1 text-right font-mono">{fmt(Number(l.lineTotal))} €</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex justify-end">
            <div className="w-56 space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-neutral-400">{t("hailParcDetail.colTotalHt")}</span>
                <span className="font-mono">{fmt(Number(data.invoice.totalHt))} €</span>
              </div>
              {data.invoice.tvaReverseCharge ? (
                <p className="text-[11px] text-neutral-500">
                  {t("hailParcDetail.tvaReverseChargeNote")}
                </p>
              ) : (
                data.invoice.tvaRate && (
                  <div className="flex justify-between">
                    <span className="text-neutral-400">TVA ({Number(data.invoice.tvaRate)}%)</span>
                    <span className="font-mono">{fmt(Number(data.invoice.tvaAmount))} €</span>
                  </div>
                )
              )}
              <div className="flex justify-between border-t border-neutral-800 pt-1 font-semibold text-lime">
                <span>{t("hailParcDetail.totalTtc")}</span>
                <span className="font-mono">{fmt(Number(data.invoice.totalTtc))} €</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
